import React, { useState, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { noop } from 'lodash/fp';

import styles from './Carousel.module.scss';
import { Icon } from '../icon/Icon';
import { CaretSide } from '../icons';

const { primary } = styles;

interface CarouselProps {
    children: JSX.Element[];
    markers: number[];
    height: number;
    width: number;
    onNavigateClick?: () => void;
    timeout?: number | null;
}

export const MARKERS_HEIGHT = 15;
export const ARROW_WIDTH_OFFSET = 28;

export const Carousel: React.FC<CarouselProps> = ({ children, markers, height, width, onNavigateClick = noop, timeout = null }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [tileLeftPosition, setTileLeftPosition] = useState('0px');
    const [carouselTimeout, setCarouselTimeout] = useState<number | null>(timeout);
    const [isPaused, setIsPaused] = useState(false);

    const totalMarkers = useMemo(() => markers.length, [markers]);
    const showArrows = useMemo(() => totalMarkers > 1, [totalMarkers]);

    const next = useCallback(() => {
        const nextIndex = (selectedIndex + 1) % totalMarkers;
        const nextLeft = (-width * nextIndex) + 'px';
        setSelectedIndex((selectedIndex + 1) % totalMarkers);
        setTileLeftPosition(nextLeft);
        onNavigateClick();
    }, [selectedIndex, totalMarkers, width, onNavigateClick]);

    const previous = useCallback(() => {
        const previousIndex = (selectedIndex + totalMarkers - 1) % totalMarkers;
        const previousLeft = (-width * previousIndex) + 'px';
        setSelectedIndex((selectedIndex + totalMarkers - 1) % totalMarkers);
        setTileLeftPosition(previousLeft);
        onNavigateClick();
    }, [selectedIndex, totalMarkers, width, onNavigateClick]);

    const selectSlide = useCallback((index: number) => {
        setSelectedIndex(index);
        const nextLeft = (-width * index) + 'px';
        setTileLeftPosition(nextLeft);
        onNavigateClick();
    }, [width, onNavigateClick]);

    const onMouseEnter = useCallback(() => setIsPaused(true), []);

    const onMouseLeave = useCallback(() => setIsPaused(false), []);

    useEffect(() => {
        if (isPaused || carouselTimeout === null) {
            return;
        }

        if (carouselTimeout <= 0) {
            next();
            setCarouselTimeout(timeout);
            return;
        }

        const interval = setInterval(() => setCarouselTimeout(prev => (prev !== null ? prev - 1000 : null)), 1000);

        return () => clearInterval(interval);

    }, [carouselTimeout, isPaused, timeout, next]);

    return (
        <div className={styles.carouselWrapper} style={{ height, width: `${width + (ARROW_WIDTH_OFFSET * 2)}px` }}>
            <div className={styles.carouselContentWrapper}>
                {showArrows ? <div onClick={previous} className={styles.previousArrow}><Icon icon={CaretSide} color={primary} /></div> : <div className={styles.placeholderArrow} />}
                <div className={styles.carousel} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                    <div className={styles.tile} style={{ left: tileLeftPosition }}>
                        {children}
                    </div>
                </div>
                {showArrows ? <div onClick={next} className={styles.nextArrow}><Icon icon={CaretSide} color={primary} /></div> : <div className={styles.placeholderArrow} />}
            </div>
            {showArrows &&
                <div className={styles.markers} style={{ top: height - MARKERS_HEIGHT, width }}>
                    {markers.map(index => (
                        <div key={index} onClick={() => selectSlide(index)} className={classnames(styles.marker, { [styles.selectedMarker]: index === selectedIndex })} />
                    ))}
                </div>
            }
        </div>
    );
};
